import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import Select from 'react-select'
import { useForm } from 'react-hook-form'
import { Table, Button } from 'react-bootstrap'
import 'font-awesome/css/font-awesome.min.css'
import DataTable from 'datatables.net-dt'
import 'datatables.net-dt/css/jquery.dataTables.min.css'
import axios from 'axios'
import SearchInput from '../ui/SearchInput'
import StatusToggle from '../ui/StatusToggle'
import ActionButtons from '../ui/ActionButtons'
import ConfirmModal from '../ui/ConfirmModal'
import AddTopic from './AddTopic'
import EditTopic from './EditTopic'

let table = null

function TopicList() {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteTopicId, setDeleteTopicId] = useState(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editTopicId, setEditTopicId] = useState(null)
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusModalAction, setStatusModalAction] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await getTopics()
      } catch (error) {
        console.error('Error loading data:', error)
        setIsLoading(false)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (data.length > 0) {
      if (table) {
        table.destroy()
      }
      table = new DataTable('#stopicTable', {
        pageLength: 100,
        dom: 't',
        initComplete: function () {
          setIsLoading(false)
        },
      })
    } else {
      setIsLoading(false)
    }
  }, [data])

  const handleDelete = (id) => {
    setDeleteTopicId(id)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      if (!deleteTopicId) return
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}delete_subtopic/${deleteTopicId}`
      )
      toast.success('Sub Topic deleted')
      await getTopics()
    } catch (error) {
      toast.error('Failed to delete sub topic')
    } finally {
      setDeleteModalOpen(false)
      setDeleteTopicId(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setDeleteTopicId(null)
  }

  const handleModalConfirm = async () => {
    try {
      if (!statusModalAction) return
      const { id, type } = statusModalAction
      const endpoint =
        type === 'block' ? `block_subtopic/${id}` : `unblock_subtopic/${id}`
      await axios.put(`${process.env.REACT_APP_BASE_URL}${endpoint}`)
      toast.success(
        type === 'block' ? 'Sub Topic deactivated' : 'Sub Topic activated'
      )
      await getTopics()
    } catch (error) {
      toast.error('Failed to update status')
    } finally {
      setStatusModalOpen(false)
      setStatusModalAction(null)
    }
  }

  const handleModalCancel = () => {
    setStatusModalOpen(false)
    setStatusModalAction(null)
  }

  const getTopics = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}getallsubtopics`
      )
      if (response.status === 200) {
        setData(response.data)
        navigate('/subtopics')
      }
    } catch (error) {
      toast.error('Failed to fetch subtopics')
    }
  }

  return (
    <section className="bg-white border rounded-[10px] border-[#F0F0F0]">
      <div className="flex justify-between items-center border px-5 py-4 border-[#F0F0F0]">
        <h2 className="text-lg text-[#030303] font-bold">Sub Topics List</h2>
        <div className="flex items-center gap-2.5">
          <SearchInput
            placeholder="Search subtopic..."
            onSearch={setSearchQuery}
            className="w-[376px] max-md:w-[300px] max-sm:w-[200px]"
          />
          <button
            className="flex items-center gap-1 bg-[#DE3C7D] text-white p-2.5 rounded-md hover:bg-[#c8356f] transition-colors"
            onClick={() => setAddModalOpen(true)}
          >
            <span className="text-xs">Add Sub Topic</span>
            <i className="fa fa-plus"></i>
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
          </div>
        ) : (
          <table className="w-full" id="stopicTable">
            <thead className="bg-neutral-50">
              <tr>
                <th className="text-sm text-[#535862] font-medium text-center border w-[100px] px-5 py-3.5 border-[#F0F0F0]">
                  S.No
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Main Topic
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Sub Topic Code
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-96 px-5 py-3.5 border-[#F0F0F0]">
                  Sub Topic Name
                </th>
                <th className="text-sm text-[#535862] font-medium text-left border w-40 px-5 py-3.5 border-[#F0F0F0]">
                  Type
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-32 px-5 py-3.5 border-[#F0F0F0]">
                  Status
                </th>
                <th className="text-sm text-[#535862] font-medium text-center border w-32 px-5 py-3.5 border-[#F0F0F0]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={item._id} className="border border-[#F0F0F0]">
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      {index + 1}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.name}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_code}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_title}
                    </td>
                    <td className="text-left border px-5 py-2 border-[#F0F0F0]">
                      {item.topic_type}
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      <StatusToggle
                        status={item.status}
                        onChange={() => {
                          setStatusModalAction({
                            id: item._id,
                            type: item.status ? 'block' : 'unblock',
                          })
                          setStatusModalOpen(true)
                        }}
                      />
                    </td>
                    <td className="text-center border px-5 py-2 border-[#F0F0F0]">
                      <ActionButtons
                        onEdit={() => {
                          setEditTopicId(item._id)
                          setEditModalOpen(true)
                        }}
                        onDelete={() => handleDelete(item._id)}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[#535862]">
                    No subtopics found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {addModalOpen && (
        <AddTopic
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => {
            setAddModalOpen(false)
            getTopics()
          }}
        />
      )}

      {editModalOpen && (
        <EditTopic
          id={editTopicId}
          onClose={() => setEditModalOpen(false)}
          onSuccess={() => {
            setEditModalOpen(false)
            getTopics()
          }}
        />
      )}

      <ConfirmModal
        open={statusModalOpen}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        title={
          statusModalAction?.type === 'block'
            ? 'Deactivate Sub Topic'
            : 'Activate Sub Topic'
        }
        message={`Are you sure you want to ${
          statusModalAction?.type === 'block' ? 'deactivate' : 'activate'
        } this Sub Topic?`}
      />

      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Sub Topic"
        message="Are you sure you want to delete this Sub Topic?"
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />

      <ToastContainer />
    </section>
  )
}

export default TopicList
